import * as signalR from '@microsoft/signalr';

const API_BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace('/api', '');

if (!API_BASE_URL) {
  throw new Error('[SignalR] VITE_API_URL environment variable is not set. Cannot connect to communication hub.');
}

const HUB_URL = `${API_BASE_URL}/hubs/communication`;

class CommunicationHubService {
  private connection: signalR.HubConnection | null = null;
  private startPromise: Promise<void> | null = null;
  private explicitlyStopped = false;
  // Always read via getAccessToken() so a proactive token refresh doesn't
  // require tearing down and reconnecting the hub.
  private getAccessToken: () => string = () => '';

  // Registered event handlers survive reconnects
  private eventHandlers: Map<string, Set<(...args: any[]) => void>> = new Map();

  // Listeners notified whenever connection state flips, so React can subscribe
  // to isConnected() reactively (e.g. via useSyncExternalStore).
  private connectionStateListeners: Set<() => void> = new Set();

  private notifyConnectionStateChange(): void {
    this.connectionStateListeners.forEach((listener) => listener());
  }

  onConnectionStateChange(listener: () => void): () => void {
    this.connectionStateListeners.add(listener);
    return () => this.connectionStateListeners.delete(listener);
  }

  async start(getAccessToken: () => string): Promise<void> {
    this.getAccessToken = getAccessToken;

    // If already connected with the same connection, nothing to do
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      console.debug('[SignalR] Already connected, skipping start');
      return;
    }

    // If a start is already in flight, wait for it
    if (this.startPromise) {
      console.debug('[SignalR] Start already in progress, awaiting');
      return this.startPromise;
    }

    this.explicitlyStopped = false;

    this.startPromise = this._doStart().finally(() => {
      this.startPromise = null;
    });

    return this.startPromise;
  }

  private async _doStart(): Promise<void> {
    // Tear down any existing connection cleanly first
    if (this.connection) {
      try { await this.connection.stop(); } catch { /* ignore */ }
      this.connection = null;
    }

    const tokenPreview = this.getAccessToken().slice(0, 20) + '...';
    console.info(`[SignalR] Starting connection | URL=${HUB_URL} | Token=${tokenPreview}`);

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => this.getAccessToken(),
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => {
          if (this.explicitlyStopped) return null;
          const delays = [0, 2000, 10000, 30000, 60000];
          const delay = delays[ctx.previousRetryCount] ?? 60000;
          console.warn(`[SignalR] Auto-reconnect attempt #${ctx.previousRetryCount + 1} in ${delay}ms`);
          return delay;
        },
      })
      .configureLogging(import.meta.env.PROD ? signalR.LogLevel.Error : signalR.LogLevel.Debug)
      .build();

    conn.onclose((err) => {
      if (err) console.error('[SignalR] Connection closed with error:', err);
      else console.info('[SignalR] Connection closed cleanly');
      this.notifyConnectionStateChange();
    });

    conn.onreconnecting((err) => {
      console.warn('[SignalR] Reconnecting...', err ?? '');
      this.notifyConnectionStateChange();
    });

    conn.onreconnected((connectionId) => {
      console.info(`[SignalR] Reconnected | ConnectionId=${connectionId}`);
      this._reattachHandlers(conn);
      this.notifyConnectionStateChange();
    });

    try {
      await conn.start();
    } catch (err) {
      console.error('[SignalR] Failed to connect:', err);
      return;
    }

    // Bail if stop() was called while we were connecting
    if (this.explicitlyStopped) {
      console.debug('[SignalR] Stopped during connect, discarding connection');
      try { await conn.stop(); } catch { /* ignore */ }
      return;
    }

    this.connection = conn;
    console.info(`[SignalR] Connected | ConnectionId=${conn.connectionId}`);
    this._attachHandlers(conn);
    this.notifyConnectionStateChange();
  }

  async stop(): Promise<void> {
    this.explicitlyStopped = true;
    const conn = this.connection;
    this.connection = null;
    if (conn && conn.state !== signalR.HubConnectionState.Disconnected) {
      try { await conn.stop(); } catch { /* ignore */ }
    }
    this.notifyConnectionStateChange();
  }

  on(eventName: string, handler: (...args: any[]) => void): void {
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, new Set());
    }
    this.eventHandlers.get(eventName)!.add(handler);
    // Register immediately if already connected
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      this.connection.on(eventName, handler);
    }
  }

  off(eventName: string, handler: (...args: any[]) => void): void {
    const handlers = this.eventHandlers.get(eventName);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) this.eventHandlers.delete(eventName);
    }
    this.connection?.off(eventName, handler);
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  async invoke<T = void>(methodName: string, ...args: unknown[]): Promise<T | null> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return await this.connection.invoke<T>(methodName, ...args);
    }
    return null;
  }

  private _attachHandlers(conn: signalR.HubConnection): void {
    this.eventHandlers.forEach((handlers, eventName) => {
      handlers.forEach((handler) => conn.on(eventName, handler));
    });
    console.debug(`[SignalR] Attached ${this.eventHandlers.size} event handler group(s)`);
  }

  private _reattachHandlers(conn: signalR.HubConnection): void {
    this.eventHandlers.forEach((handlers, eventName) => {
      handlers.forEach((handler) => {
        conn.off(eventName, handler);
        conn.on(eventName, handler);
      });
    });
    console.debug(`[SignalR] Reattached ${this.eventHandlers.size} event handler group(s) after reconnect`);
  }
}

export const communicationHub = new CommunicationHubService();
